import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { Button } from "@/components/ui/button"
import {
  ArrowRight,
  Database,
  GitBranch,
  Share2,
  BarChart3,
  Lock,
  Printer,
} from "lucide-react"

import { Link } from "react-router-dom"

import { SEO } from "@/components/SEO"
import { PageFAQ } from "@/components/PageFAQ"
import { predictiveFabricsFAQs } from "@/data/faqData"

export default function PredictiveFabrics() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <SEO
        title="Predictive Fabrics | Intelligent Data Mesh | RSA Tech Group"
        description="Unifying siloed data into a fluid, intelligent mesh that powers real-time insights, federated governance, and predictive analytics."
        image="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=2600"
      />
      <Navigation />

      {/* Print/Download Button */}
      <button
        onClick={() => window.print()}
        className="fixed top-32 right-6 z-50 bg-gradient-to-r from-blue-600 to-orange-500 text-white px-4 py-2 rounded-full hover:scale-105 transition-all flex items-center gap-2 print:hidden shadow-xl shadow-blue-900/20 group animate-fade-in"
        title="Save Case Study as PDF"
      >
        <Printer className="w-4 h-4" />
        <span className="text-sm font-bold hidden md:inline">Save as PDF</span>
      </button>

      {/* Hero Section */}
      <div className="relative pt-32 pb-24 bg-primary overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.1)_0%,transparent_70%)] opacity-30 pointer-events-none" />
        <div className="section-container relative z-10 text-center">
          <h1 className="text-4xl md:text-6xl font-black text-white tracking-tight mb-8">
            Predictive <span className="text-accent">Fabrics</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-10">
            Unifying siloed data into a fluid, intelligent mesh that powers
            real-time insights.
          </p>
        </div>
      </div>

      {/* Content Section */}
      <section className="py-20 bg-gray-50">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
                Weaving Data into Intelligence
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Data silos kill innovation. We build Predictive Fabrics -
                intelligent data meshes that connect every strand of information
                across your organization.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                By democratizing data access and applying real-time governance,
                we turn your raw data into a predictive asset that fuels better
                decisions and automated actions.
              </p>

              <div className="grid grid-cols-1 gap-4 mb-8">
                {[
                  { icon: Share2, text: "Enterprise Data Mesh" },
                  { icon: GitBranch, text: "Real-time Stream Processing" },
                  { icon: Lock, text: "Federated Data Governance" },
                  { icon: BarChart3, text: "Predictive Analytics Models" },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center text-primary font-medium p-4 bg-white rounded-lg shadow-sm border border-gray-100"
                  >
                    <div className="p-2 bg-accent/10 rounded-md mr-4">
                      <item.icon className="h-5 w-5 text-accent" />
                    </div>
                    {item.text}
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-accent/10 rounded-lg transform translate-x-4 translate-y-4" />
              <img
                src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=2600"
                alt="Abstract Data Visualization"
                className="rounded-lg shadow-xl relative z-10 w-full object-cover h-[500px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-white">
        <div className="section-container">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-primary mb-6">
              Data Architecture
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Data Lakes & Warehouses",
                desc: "Unified storage architectures that handle structured and unstructured data at scale.",
                img: "https://images.unsplash.com/photo-1542744094-24638eff58bb",
              },
              {
                title: "Business Intelligence",
                desc: "Visual dashboards that provide actionable insights across all departments.",
                img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71",
              },
              {
                title: "Machine Learning Ops",
                desc: "Standardized pipelines for training, deploying, and monitoring ML models.",
                img: "https://images.unsplash.com/photo-1527474305487-b87b222841cc",
              },
            ].map((card, i) => (
              <div
                key={i}
                className="group rounded-xl overflow-hidden shadow-lg border border-gray-100"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={card.img + "?auto=format&fit=crop&q=80&w=800"}
                    alt={card.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-primary mb-3">
                    {card.title}
                  </h3>
                  <p className="text-gray-600">{card.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-8"
              >
                Unify Your Data <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PageFAQ
        faqs={predictiveFabricsFAQs}
        title="Predictive Fabrics FAQs"
        subtitle="Common questions about data mesh and intelligent data architecture"
        variant="light"
      />

      <Footer />
    </div>
  )
}
